/**
 * travaux-particuliers.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration centralisée des pages "stubs" /particuliers/travaux/[slug].
 *
 * Chaque entrée décrit une opération de travaux résidentielle
 * éligible aux aides à la rénovation. Le rôle de
 * ces pages est de :
 *
 *   1. Présenter l'opération avec un ton particulier (vocabulaire grand
 *      public, pas de jargon technique).
 *   2. Mettre en avant la CUMULABILITÉ des aides.
 *   3. Renvoyer vers la fiche technique correspondante.
 *
 * Tous les contenus restent prudents (formulations "jusqu'à",
 * "selon le profil").
 */

export interface TravailStubKeyPoint {
  iconName: string;
  label: string;
  description: string;
}

export interface TravailStubStep {
  title: string;
  description: string;
}

export interface TravailStubFAQ {
  question: string;
  answer: string;
}

export type TravailCumulTone = 'brand' | 'accent' | 'violet' | 'sky' | 'amber';

export interface TravailStubCumulable {
  /** Nom court de l'aide */
  label: string;
  /** Mini-précision */
  detail: string;
  /** Slug interne de la page d'aide (/particuliers/aides/<slug>). */
  slug?: string;
  tone: TravailCumulTone;
}

export interface TravailStub {
  /** URL : /particuliers/travaux/<slug>. */
  slug: string;
  /** Code de la fiche technique officielle (ex. "BAR-TH-171"). */
  ficheCode: string;
  /** Label court pour la fiche technique (affiché à côté du code). */
  ficheLabel: string;
  /** Catégorie d'opération ("Isolation", "Chauffage & ECS", "Ventilation"). */
  category: 'isolation' | 'chauffage' | 'ecs' | 'ventilation';
  iconName: string;
  eyebrow: string;
  title: string;
  /** Libellé court pour la FAQ */
  shortLabel: string;
  lead: string;
  /** Indicateur de tête */
  badge: string;
  badgeLabel: string;
  keyPoints: TravailStubKeyPoint[];
  cumulableWith: TravailStubCumulable[];
  steps: TravailStubStep[];
  faq: TravailStubFAQ[];
  ctaLabel: string;
  ctaHref: string;
  secondaryCta?: { label: string; href: string };

  /* ─── Ligne éditoriale — bénéfice utilisateur chiffré ─── */
  /** Promesse principale */
  effyPromise: string;
  /** Bénéfice émotionnel court */
  comfortBenefit: string;
  /** Économies % indicatives sur le poste concerné */
  savingsPercent: number;
  /** Label du poste concerné */
  savingsLabel: string;
  /** Aide max indicative (€) */
  maxAidEur: number;
  /** Coût travaux indicatif (€ TTC pose comprise) */
  avgCostEur: { min: number; max: number };
}

/* ─── Aides cumulables ──────── */

const MPR: TravailStubCumulable = {
  label: "MaPrimeRénov'",
  detail: "selon vos revenus et votre projet",
  slug: 'maprimerenov',
  tone: 'brand',
};

const CEE: TravailStubCumulable = {
  label: 'Prime énergie',
  detail: 'sans condition de ressources',
  slug: 'cee',
  tone: 'accent',
};

const ECO_PTZ: TravailStubCumulable = {
  label: 'Éco-PTZ',
  detail: "prêt sans intérêt jusqu'à 50 000 €",
  slug: 'eco-ptz',
  tone: 'violet',
};

const TVA_55: TravailStubCumulable = {
  label: 'TVA 5,5 %',
  detail: "14,5 points d'économie sur la facture",
  slug: 'tva-reduite',
  tone: 'sky',
};

const COUP_DE_POUCE: TravailStubCumulable = {
  label: 'Coup de pouce',
  detail: 'prime renforcée pour certains travaux',
  slug: 'coup-de-pouce',
  tone: 'amber',
};

/* ─── Liste des travaux résidentiels ─────────────── */

export const TRAVAUX_PARTICULIERS: TravailStub[] = [
  // ──────────────────────────────────────────────────────────────
  //  ISOLATION
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'isolation-combles',
    ficheCode: 'BAR-EN-101',
    ficheLabel: 'Isolation des combles ou de toitures',
    category: 'isolation',
    iconName: 'home',
    eyebrow: 'Isolation thermique, Toiture',
    title: 'Isolation des combles',
    shortLabel: "l'isolation des combles",
    lead: "Isoler les combles, c'est le geste le plus rentable : jusqu'à 30 % de la chaleur s'échappe par le toit. Résultat : une maison plus chaude l'hiver, plus fraîche l'été, et une facture de chauffage qui baisse.",
    badge: 'Cumul aides',
    badgeLabel: 'Aides cumulables sur la même opération',
    keyPoints: [
      {
        iconName: 'thermometer',
        label: "Jusqu'à 30 % de chaleur conservée",
        description: "La chaleur monte : isoler la toiture est le premier chantier pour faire baisser la facture.",
      },
      {
        iconName: 'shield-check',
        label: 'Isolation performante exigée',
        description: "Pour toucher les aides, l'isolant doit respecter un niveau de performance élevé. On vous guide sur le bon choix.",
      },
      {
        iconName: 'hard-hat',
        label: 'Professionnel qualifié',
        description: 'Les aides demandent un artisan certifié. On vous met en relation avec le bon professionnel.',
      },
      {
        iconName: 'sparkles',
        label: 'Bonus revenus modestes',
        description: 'Des aides renforcées existent si vos ressources sont limitées. On vérifie votre éligibilité.',
      },
    ],
    cumulableWith: [MPR, CEE, ECO_PTZ, TVA_55, COUP_DE_POUCE],
    steps: [
      {
        title: 'Diagnostic et devis',
        description: "Un artisan évalue l'isolant existant et vous propose la solution adaptée (laine de verre, ouate de cellulose…).",
      },
      {
        title: 'Demande des primes',
        description: "Les primes doivent être demandées avant de signer le devis. On s'occupe de cette formalité pour vous.",
      },
      {
        title: 'Réalisation des travaux',
        description: "Pose de l'isolant en combles perdus (soufflage) ou aménagés (panneaux), selon votre configuration.",
      },
      {
        title: 'Versement des aides',
        description: "Les primes arrivent après la fin des travaux. On vous tient informé à chaque étape.",
      },
    ],
    faq: [
      {
        question: "Combien coûte l'isolation des combles ?",
        answer:
          "Environ 20 à 50 € par m² pour des combles perdus (soufflage) et 40 à 90 € par m² pour des combles aménagés. Les aides cumulées peuvent couvrir une grande partie du coût, surtout pour les ménages modestes.",
      },
      {
        question: 'Combles perdus ou aménagés, quelle différence ?',
        answer:
          "Les combles perdus ne sont pas habitables : on isole au sol par soufflage. Les combles aménagés sont habitables : on isole sous la toiture. Le coût et les aides diffèrent selon le cas.",
      },
      {
        question: 'Peut-on cumuler toutes les aides ?',
        answer:
          "Oui. L'aide de l'État, la prime énergie et le prêt sans intérêt se cumulent. Pour un ménage modeste, le cumul peut couvrir 80 à 100 % du coût. Le prêt permet de financer le reste sans avancer d'argent.",
      },
    ],
    ctaLabel: 'Calculer mes aides isolation',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Voir la fiche BAR-EN-101', href: '/cee/fiches/BAR-EN-101' },
    effyPromise: 'Votre chauffage vous coûte trop cher ?',
    comfortBenefit: 'Chaleur retenue chez vous, facture allégée',
    savingsPercent: 30,
    savingsLabel: 'de chaleur évitée par la toiture',
    maxAidEur: 4000,
    avgCostEur: { min: 2000, max: 5000 },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'isolation-murs',
    ficheCode: 'BAR-EN-102',
    ficheLabel: 'Isolation des murs',
    category: 'isolation',
    iconName: 'shield-check',
    eyebrow: 'Isolation thermique, Murs',
    title: 'Isolation des murs',
    shortLabel: "l'isolation des murs",
    lead: "Vos murs laissent partir 20 à 25 % de la chaleur. Deux solutions : l'isolation par l'intérieur (moins chère) ou par l'extérieur (plus efficace). Les deux donnent droit aux aides.",
    badge: 'Cumul aides',
    badgeLabel: 'Aides cumulables sur la même opération',
    keyPoints: [
      {
        iconName: 'thermometer',
        label: '20 à 25 % de chaleur conservée',
        description: "Isoler les murs améliore le confort thermique en hiver comme en été.",
      },
      {
        iconName: 'shield-check',
        label: 'Isolation performante exigée',
        description: "Un niveau de performance minimum est requis pour bénéficier des aides.",
      },
      {
        iconName: 'hammer',
        label: 'Intérieur ou extérieur selon votre projet',
        description: "L'isolation extérieure conserve la surface habitable et traite mieux les ponts thermiques. L'isolation intérieure est moins onéreuse mais réduit la surface.",
      },
      {
        iconName: 'hard-hat',
        label: 'Professionnel qualifié',
        description: "L'installateur doit être certifié pour que vous bénéficiez des aides.",
      },
    ],
    cumulableWith: [MPR, CEE, ECO_PTZ, TVA_55],
    steps: [
      {
        title: 'Diagnostic et choix de technique',
        description: "Un professionnel évalue l'état des murs et propose la technique la plus adaptée à votre logement et budget.",
      },
      {
        title: 'Demande des primes',
        description: "La demande de prime énergie doit être faite avant la signature du devis.",
      },
      {
        title: 'Réalisation des travaux',
        description: "Pose de l'isolant (en extérieur : panneaux fixés à la façade + enduit ou bardage ; en intérieur : ossature + laine + parement).",
      },
      {
        title: 'Versement des aides',
        description: "Les primes sont versées après réception du dossier complet.",
      },
    ],
    faq: [
      {
        question: 'Intérieur ou extérieur : quelle technique choisir ?',
        answer:
          "L'isolation extérieure est plus performante car elle supprime les ponts thermiques et n'empiète pas sur la surface habitable. Elle coûte plus cher (100 à 200 €/m²) mais bénéficie d'aides plus généreuses. L'isolation intérieure coûte 50 à 100 €/m² mais réduit la surface intérieure.",
      },
      {
        question: "Faut-il une autorisation pour l'isolation extérieure ?",
        answer:
          "L'isolation extérieure modifie l'aspect du bâtiment : une déclaration préalable de travaux est généralement requise en mairie. Dans certains secteurs protégés, des contraintes esthétiques peuvent s'appliquer.",
      },
      {
        question: 'Quel montant de prime pour isoler les murs ?',
        answer:
          "L'aide peut couvrir 15 à 75 €/m² selon vos revenus et la technique, et la prime énergie 10 à 25 €/m² supplémentaires. Pour une façade de 100 m², le cumul peut représenter 2 500 € à 10 000 € d'aides.",
      },
    ],
    ctaLabel: 'Calculer mes aides isolation murs',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Voir la fiche BAR-EN-102', href: '/cee/fiches/BAR-EN-102' },
    effyPromise: 'Vos murs ne devraient pas être glacés en hiver',
    comfortBenefit: 'Des murs qui ne sont plus glacés en hiver',
    savingsPercent: 25,
    savingsLabel: 'de déperditions thermiques évitées',
    maxAidEur: 10000,
    avgCostEur: { min: 5000, max: 20000 },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'isolation-sols',
    ficheCode: 'BAR-EN-103',
    ficheLabel: "Isolation d'un plancher bas",
    category: 'isolation',
    iconName: 'layers',
    eyebrow: 'Isolation thermique, Plancher bas',
    title: 'Isolation des sols',
    shortLabel: "l'isolation des sols",
    lead: "L'isolation du plancher bas (sol sur cave, sur vide sanitaire ou sur terre-plein) supprime la sensation de pieds froids et réduit de 7 à 10 % les déperditions thermiques. C'est souvent l'opération la plus simple à réaliser, notamment quand un sous-sol non chauffé est accessible.",
    badge: 'Cumul aides',
    badgeLabel: 'Aides cumulables sur la même opération',
    keyPoints: [
      {
        iconName: 'thermometer',
        label: '7 à 10 % de chaleur conservée',
        description: "Le sol reste le poste où l'isolation est la moins coûteuse à mettre en œuvre, surtout par le dessous (cave ou vide sanitaire).",
      },
      {
        iconName: 'shield-check',
        label: 'Isolation performante exigée',
        description: "Un niveau de performance minimum est requis pour les aides.",
      },
      {
        iconName: 'home',
        label: 'Par le dessous si possible',
        description: "Pose au plafond de la cave ou du vide sanitaire : pas de travaux à l'intérieur, pas de perte de hauteur sous plafond.",
      },
      {
        iconName: 'hard-hat',
        label: 'Professionnel qualifié',
        description: "Un artisan certifié est nécessaire pour bénéficier des aides.",
      },
    ],
    cumulableWith: [MPR, CEE, ECO_PTZ, TVA_55],
    steps: [
      {
        title: 'Diagnostic du plancher',
        description: "L'artisan évalue le type de plancher (sur cave, vide sanitaire, terre-plein) et la technique adaptée.",
      },
      {
        title: 'Demande des primes',
        description: "La demande de prime énergie doit être faite avant la signature du devis.",
      },
      {
        title: "Pose de l'isolant",
        description: "Par le dessous (panneaux fixés au plafond du sous-sol) ou par le dessus (chape isolante) selon l'accessibilité.",
      },
      {
        title: 'Versement des aides',
        description: "Les primes sont versées après réception du dossier complet.",
      },
    ],
    faq: [
      {
        question: 'Peut-on isoler le sol par le dessus ?',
        answer:
          "Oui, mais cela implique de retirer le revêtement existant et réduit la hauteur sous plafond. Quand un sous-sol ou un vide sanitaire est accessible, l'isolation par le dessous est nettement plus simple, moins chère et sans perturbation.",
      },
      {
        question: "Combien coûte l'isolation d'un plancher bas ?",
        answer:
          "Environ 20 à 50 €/m² pour la pose par le dessous. C'est souvent le geste d'isolation le moins onéreux et qui bénéficie tout de même d'aides cumulables.",
      },
      {
        question: "L'isolation du sol est-elle compatible avec un plancher chauffant ?",
        answer:
          "Oui, et c'est même fortement recommandé. Sans isolation sous-jacente, une grande partie de la chaleur produite par le plancher chauffant se perd vers le sous-sol. L'isolation améliore considérablement le rendement du chauffage.",
      },
    ],
    ctaLabel: 'Calculer mes aides isolation sols',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Voir la fiche BAR-EN-103', href: '/cee/fiches/BAR-EN-103' },
    effyPromise: 'Fini la sensation de pieds glacés le matin',
    comfortBenefit: 'Plancher tempéré, confort immédiat',
    savingsPercent: 10,
    savingsLabel: 'de déperditions thermiques en moins',
    maxAidEur: 2000,
    avgCostEur: { min: 2000, max: 5000 },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'isolation-fenetres',
    ficheCode: 'BAR-EN-104',
    ficheLabel: 'Remplacement de fenêtres ou portes-fenêtres',
    category: 'isolation',
    iconName: 'layers',
    eyebrow: 'Menuiseries, Double vitrage',
    title: 'Remplacement des fenêtres',
    shortLabel: 'le remplacement des fenêtres',
    lead: "Vos fenêtres laissent partir 10 à 15 % de la chaleur si elles sont mal isolées. Passer du simple vitrage au double vitrage à isolation renforcée améliore le confort thermique, réduit le bruit et valorise votre bien. Éligible aux aides.",
    badge: 'Cumul aides',
    badgeLabel: 'Aides cumulables sur la même opération',
    keyPoints: [
      {
        iconName: 'thermometer',
        label: '10 à 15 % de chaleur conservée',
        description: "Surtout en remplaçant du simple vitrage par du double vitrage performant.",
      },
      {
        iconName: 'shield-check',
        label: 'Double vitrage performant exigé',
        description: "Des seuils techniques minimums sont requis pour bénéficier des aides.",
      },
      {
        iconName: 'wind',
        label: 'Confort acoustique amélioré',
        description: "Le double vitrage moderne réduit nettement les nuisances sonores extérieures.",
      },
      {
        iconName: 'hard-hat',
        label: 'Professionnel qualifié',
        description: "Votre installateur doit être certifié pour que vous bénéficiez des aides.",
      },
    ],
    cumulableWith: [MPR, CEE, ECO_PTZ, TVA_55],
    steps: [
      {
        title: 'Mesure et choix des matériaux',
        description: "L'artisan prend les cotes et propose les fenêtres adaptées (PVC, aluminium, bois) selon votre budget.",
      },
      {
        title: 'Demande des primes',
        description: "La demande de prime énergie doit être faite avant la signature du devis.",
      },
      {
        title: 'Dépose et pose',
        description: "Démontage des anciennes menuiseries, pose des nouvelles avec joint d'étanchéité.",
      },
      {
        title: 'Versement des aides',
        description: "Les primes sont versées après transmission de la facture.",
      },
    ],
    faq: [
      {
        question: 'Le remplacement des fenêtres est-il rentable ?',
        answer:
          "Sur le plan énergétique seul, le retour sur investissement est long (15 à 25 ans). Mais l'opération apporte aussi du confort thermique et acoustique immédiat, et valorise votre bien. Avec le cumul des aides, le reste à charge devient acceptable.",
      },
      {
        question: 'Faut-il remplacer toutes les fenêtres en même temps ?',
        answer:
          "Pas obligatoirement, mais tout faire en une fois évite les frais fixes répétés et améliore l'étanchéité globale. Si le budget est contraint, commencez par les fenêtres les plus exposées au nord.",
      },
      {
        question: 'Quel montant de prime pour des fenêtres ?',
        answer:
          "La prime énergie peut couvrir 40 à 100 € par fenêtre, et l'aide de l'État 40 à 100 € supplémentaires selon vos revenus. Pour 8 fenêtres, le cumul peut représenter 1 000 à 1 500 € d'aides.",
      },
    ],
    ctaLabel: 'Calculer mes aides fenêtres',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Voir la fiche BAR-EN-104', href: '/cee/fiches/BAR-EN-104' },
    effyPromise: 'Confort thermique et silence retrouvés',
    comfortBenefit: 'Silence dans la maison + chaleur conservée',
    savingsPercent: 15,
    savingsLabel: 'de chaleur conservée',
    maxAidEur: 1500,
    avgCostEur: { min: 3000, max: 15000 },
  },
  // ──────────────────────────────────────────────────────────────
  //  CHAUFFAGE & ECS
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'pompe-a-chaleur',
    ficheCode: 'BAR-TH-171',
    ficheLabel: 'Pompe à chaleur air/eau résidentielle',
    category: 'chauffage',
    iconName: 'snowflake',
    eyebrow: 'Chauffage performant',
    title: 'Pompe à chaleur air/eau',
    shortLabel: 'la pompe à chaleur air/eau',
    lead: "La pompe à chaleur air/eau remplace avantageusement une chaudière fioul ou gaz : pour 1 kWh d'électricité consommé, elle restitue 3 à 4 kWh de chaleur. C'est l'opération la mieux soutenue par les aides, avec un bonus renforcé pour les ménages modestes qui remplacent un chauffage fossile.",
    badge: 'Aides maximales',
    badgeLabel: 'Aides maximales pour le chauffage',
    keyPoints: [
      {
        iconName: 'snowflake',
        label: 'Performance garantie',
        description: "Un coefficient de performance minimum est exigé pour bénéficier des aides.",
      },
      {
        iconName: 'flame',
        label: 'Remplacement chauffage fossile',
        description: "L'opération est la mieux valorisée quand elle remplace une chaudière fioul, charbon ou gaz.",
      },
      {
        iconName: 'sparkles',
        label: 'Bonus chauffage',
        description: "Aide renforcée pour les ménages aux revenus modestes qui abandonnent un chauffage fossile.",
      },
      {
        iconName: 'hard-hat',
        label: 'Professionnel certifié',
        description: "Une certification spécifique pompe à chaleur est exigée pour les aides.",
      },
    ],
    cumulableWith: [MPR, CEE, COUP_DE_POUCE, ECO_PTZ, TVA_55],
    steps: [
      {
        title: 'Étude thermique préalable',
        description: "L'installateur dimensionne la pompe à chaleur en fonction des besoins de chauffage de votre logement (déperditions, surface, isolation).",
      },
      {
        title: 'Demande des primes',
        description: "La demande de prime doit être faite avant la signature du devis.",
      },
      {
        title: 'Installation par un professionnel certifié',
        description: "Dépose de l'ancien équipement, raccordement de la pompe à chaleur au circuit hydraulique, mise en service et réglage.",
      },
      {
        title: 'Versement des aides',
        description: "Les primes sont versées sur présentation des justificatifs.",
      },
    ],
    faq: [
      {
        question: 'Combien coûte une pompe à chaleur air/eau ?',
        answer:
          "Environ 10 000 à 18 000 € pose comprise pour une maison individuelle moyenne. Le cumul des aides peut couvrir 5 000 à 12 000 € pour les ménages modestes, avec le prêt sans intérêt pour financer le reste.",
      },
      {
        question: 'Quelle pompe à chaleur choisir : air/eau ou air/air ?',
        answer:
          "La pompe à chaleur air/eau alimente le circuit de chauffage central existant (radiateurs, plancher chauffant) — c'est elle qui est éligible aux aides principales. La PAC air/air (climatisation réversible) souffle directement de l'air chaud/froid et n'ouvre droit qu'à certaines aides.",
      },
      {
        question: 'Peut-on cumuler les aides sur une pompe à chaleur ?',
        answer:
          "Oui, intégralement. Pour un ménage très modeste remplaçant une chaudière fioul par une pompe à chaleur air/eau, le total des aides peut atteindre 70 à 90 % du coût. Notre calculateur chiffre précisément le cumul selon votre situation.",
      },
    ],
    ctaLabel: 'Calculer mes aides PAC',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Voir la fiche BAR-TH-171', href: '/cee/fiches/BAR-TH-171' },
    effyPromise: 'Chauffez-vous pour beaucoup moins cher',
    comfortBenefit: "Chaud l'hiver, frais l'été avec un seul appareil",
    savingsPercent: 60,
    savingsLabel: 'sur votre facture de chauffage',
    maxAidEur: 12000,
    avgCostEur: { min: 10000, max: 18000 },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'poele-a-granules',
    ficheCode: 'BAR-TH-112',
    ficheLabel: 'Appareil indépendant de chauffage au bois',
    category: 'chauffage',
    iconName: 'flame',
    eyebrow: 'Chauffage performant, Biomasse',
    title: 'Poêle à granulés',
    shortLabel: 'le poêle à granulés',
    lead: "Le poêle à granulés (ou pellets) tourne avec un combustible renouvelable et bon marché, pour un rendement supérieur à 90 %. Éligible aux aides, il fait sens en maison individuelle, en appoint ou en chauffage principal, et pour remplacer un vieux poêle à bûches.",
    badge: 'Cumul aides',
    badgeLabel: 'Aides cumulables, appareil performant',
    keyPoints: [
      {
        iconName: 'flame',
        label: 'Rendement supérieur à 87 %',
        description: "Seuil de performance minimum exigé pour les aides — label environnemental 7 étoiles ou équivalent.",
      },
      {
        iconName: 'sparkles',
        label: 'Bonus chauffage',
        description: "Aide renforcée pour les ménages modestes qui abandonnent un chauffage fossile.",
      },
      {
        iconName: 'thermometer',
        label: 'Combustible renouvelable',
        description: "Les granulés sont produits à partir de sciures et résidus de bois — bilan carbone neutre.",
      },
      {
        iconName: 'hard-hat',
        label: 'Professionnel certifié',
        description: "Une certification spécifique appareils bois est exigée pour les aides.",
      },
    ],
    cumulableWith: [MPR, CEE, COUP_DE_POUCE, ECO_PTZ, TVA_55],
    steps: [
      {
        title: "Étude d'implantation et ramonage",
        description: "Vérification du conduit existant (tubage requis) ou installation d'un conduit neuf.",
      },
      {
        title: 'Demande des primes',
        description: "La demande de prime doit être faite avant la signature du devis.",
      },
      {
        title: 'Pose du poêle',
        description: "Installation par un professionnel certifié, mise en service et explication des consignes de fonctionnement.",
      },
      {
        title: 'Versement des aides',
        description: "Les primes sont versées après réception des justificatifs.",
      },
    ],
    faq: [
      {
        question: 'Combien coûte un poêle à granulés ?',
        answer:
          "Environ 3 500 à 7 000 € pose comprise. Le cumul des aides peut couvrir 1 500 à 4 500 € selon vos revenus et l'ancien équipement remplacé.",
      },
      {
        question: 'Faut-il un conduit de fumée existant ?',
        answer:
          "Pas obligatoirement. S'il existe un conduit (cheminée maçonnée), il faut le faire tuber. Sinon, un conduit isolé peut être créé en façade ou en toiture. Le coût varie en conséquence.",
      },
      {
        question: 'Le poêle à granulés peut-il chauffer toute la maison ?',
        answer:
          "En chauffage principal, oui, si la maison est bien distribuée (pièces ouvertes, cage d'escalier centrale). Pour une maison à étages avec couloirs fermés, le poêle reste un excellent chauffage d'appoint à compléter par d'autres émetteurs.",
      },
    ],
    ctaLabel: 'Calculer mes aides poêle granulés',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Voir la fiche BAR-TH-112', href: '/cee/fiches/BAR-TH-112' },
    effyPromise: 'Une chaleur agréable pour bien moins cher',
    comfortBenefit: 'Une flamme conviviale et bon marché',
    savingsPercent: 50,
    savingsLabel: 'sur votre facture de chauffage',
    maxAidEur: 4500,
    avgCostEur: { min: 3500, max: 7000 },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'chauffe-eau-thermodynamique',
    ficheCode: 'BAR-TH-148',
    ficheLabel: 'Chauffe-eau thermodynamique',
    category: 'ecs',
    iconName: 'droplet',
    eyebrow: 'Eau chaude sanitaire',
    title: 'Chauffe-eau thermodynamique',
    shortLabel: 'le chauffe-eau thermodynamique',
    lead: "Le chauffe-eau thermodynamique fonctionne comme une mini pompe à chaleur : il puise les calories de l'air (ambiant, extérieur ou extrait par la ventilation) pour chauffer l'eau sanitaire. Il consomme 2 à 3 fois moins d'électricité qu'un cumulus classique. Éligible aux aides, c'est un excellent geste pour réduire la facture d'eau chaude.",
    badge: 'Cumul aides',
    badgeLabel: 'Aides cumulables, performance garantie',
    keyPoints: [
      {
        iconName: 'droplet',
        label: 'Performance garantie',
        description: "Un coefficient de performance minimum est exigé pour bénéficier des aides.",
      },
      {
        iconName: 'thermometer',
        label: "60 à 70 % d'économie sur l'eau chaude",
        description: "Par rapport à un chauffe-eau électrique classique. L'eau chaude représente 10 à 15 % de la facture énergétique.",
      },
      {
        iconName: 'home',
        label: 'Installation simple',
        description: "Remplacement direct d'un cumulus existant si l'emplacement est adapté.",
      },
      {
        iconName: 'hard-hat',
        label: 'Professionnel qualifié',
        description: "Un professionnel certifié est nécessaire pour bénéficier des aides.",
      },
    ],
    cumulableWith: [MPR, CEE, ECO_PTZ, TVA_55],
    steps: [
      {
        title: 'Choix du modèle adapté',
        description: "Le professionnel évalue le local d'installation et propose le modèle adapté (air ambiant, air extrait, air extérieur).",
      },
      {
        title: 'Demande des primes',
        description: "La demande de prime doit être faite avant la signature du devis.",
      },
      {
        title: 'Installation et mise en service',
        description: "Dépose du cumulus existant, pose du nouveau ballon thermodynamique, raccordement hydraulique et électrique.",
      },
      {
        title: 'Versement des aides',
        description: "Les primes sont versées après transmission des justificatifs.",
      },
    ],
    faq: [
      {
        question: 'Combien coûte un chauffe-eau thermodynamique ?',
        answer:
          "Environ 2 500 à 4 500 € pose comprise pour un ballon de 200 à 270 L. Le cumul des aides peut couvrir 600 à 1 600 € selon vos revenus.",
      },
      {
        question: 'Quel emplacement choisir ?',
        answer:
          "Pour une version air ambiant : un local non chauffé d'au moins 20 m³ (garage, cellier, sous-sol). Pour une version air extrait : raccordement à la ventilation. Pour air extérieur : raccordement par gaines vers l'extérieur. Le choix dépend de votre logement.",
      },
      {
        question: 'Le ballon thermodynamique fait-il du bruit ?',
        answer:
          "Il génère un léger bruit de compresseur (30 à 45 dB selon les modèles, équivalent à un réfrigérateur). À éviter dans une pièce de vie. Les modèles récents sont de plus en plus silencieux.",
      },
    ],
    ctaLabel: 'Calculer mes aides chauffe-eau thermo',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Voir la fiche BAR-TH-148', href: '/cee/fiches/BAR-TH-148' },
    effyPromise: "De l'eau chaude trois fois moins chère",
    comfortBenefit: "Eau chaude trois fois moins chère qu'un cumulus classique",
    savingsPercent: 70,
    savingsLabel: "sur votre facture d'eau chaude",
    maxAidEur: 1600,
    avgCostEur: { min: 2500, max: 4500 },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'chauffe-eau-solaire',
    ficheCode: 'BAR-TH-101',
    ficheLabel: 'Chauffe-eau solaire individuel (CESI)',
    category: 'ecs',
    iconName: 'sun',
    eyebrow: 'Eau chaude sanitaire, Solaire thermique',
    title: 'Chauffe-eau solaire',
    shortLabel: 'le chauffe-eau solaire',
    lead: "Le chauffe-eau solaire capte la chaleur du soleil par des panneaux en toiture pour couvrir 50 à 70 % de votre eau chaude annuelle. Plus efficace dans le sud, il reste pertinent partout en France avec un appoint électrique ou gaz. Éligible aux aides.",
    badge: 'Cumul aides',
    badgeLabel: 'Aides cumulables, énergie solaire',
    keyPoints: [
      {
        iconName: 'sun',
        label: "50 à 70 % d'eau chaude solaire",
        description: "Couverture annuelle moyenne selon la région et le dimensionnement du système.",
      },
      {
        iconName: 'shield-check',
        label: 'Capteurs certifiés',
        description: "Des certifications spécifiques sont exigées pour bénéficier des aides.",
      },
      {
        iconName: 'home',
        label: 'Toiture orientée sud-est à sud-ouest',
        description: "Inclinaison idéale 30 à 45° et exposition sans ombre portée significative sur les capteurs.",
      },
      {
        iconName: 'hard-hat',
        label: 'Professionnel qualifié',
        description: "Une certification spécifique solaire est requise pour les aides.",
      },
    ],
    cumulableWith: [MPR, CEE, ECO_PTZ, TVA_55],
    steps: [
      {
        title: 'Étude solaire et dimensionnement',
        description: "Évaluation de l'orientation, l'inclinaison, l'ombrage et calcul de la surface de capteurs nécessaire.",
      },
      {
        title: 'Demande des primes',
        description: "La demande de prime doit être faite avant la signature du devis.",
      },
      {
        title: 'Pose des capteurs et du ballon',
        description: "Installation des panneaux thermiques en toiture, pose du ballon solaire et de la station hydraulique.",
      },
      {
        title: 'Versement des aides',
        description: "Les primes sont versées après transmission des justificatifs.",
      },
    ],
    faq: [
      {
        question: 'Le chauffe-eau solaire est-il rentable dans le Nord ?',
        answer:
          "Oui, mais le retour sur investissement est plus long que dans le Sud. À Lille ou Paris, on couvre encore 40 à 50 % des besoins annuels en eau chaude solaire — c'est moins qu'à Marseille (70 %), mais les aides rendent l'investissement intéressant partout en France.",
      },
      {
        question: 'Combien coûte un chauffe-eau solaire ?',
        answer:
          "Environ 4 500 à 7 500 € pose comprise pour un système 4 m² de capteurs + ballon 300 L (foyer de 4 personnes). Le cumul des aides peut couvrir 1 500 à 4 000 € selon vos revenus.",
      },
      {
        question: "Faut-il un système d'appoint ?",
        answer:
          "Oui, indispensable. Le solaire seul ne couvre jamais 100 % des besoins (hiver, périodes nuageuses). L'appoint peut être électrique, gaz ou couplé à la pompe à chaleur. Le système solaire reste prioritaire et l'appoint ne se déclenche qu'en complément.",
      },
    ],
    ctaLabel: 'Calculer mes aides solaire thermique',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Voir la fiche BAR-TH-101', href: '/cee/fiches/BAR-TH-101' },
    effyPromise: "De l'eau chaude grâce au soleil, gratuitement",
    comfortBenefit: "Eau chaude solaire toute l'année",
    savingsPercent: 70,
    savingsLabel: 'de votre eau chaude couverte par le soleil',
    maxAidEur: 4000,
    avgCostEur: { min: 4500, max: 7500 },
  },
  // ──────────────────────────────────────────────────────────────
  //  VENTILATION
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'vmc-double-flux',
    ficheCode: 'BAR-TH-125',
    ficheLabel: 'Système de ventilation double flux autoréglable ou modulé à haute performance',
    category: 'ventilation',
    iconName: 'wind',
    eyebrow: 'Ventilation, Renouvellement d\'air',
    title: 'VMC double flux',
    shortLabel: 'la VMC double flux',
    lead: "La VMC double flux récupère 70 à 90 % des calories de l'air extrait pour préchauffer l'air neuf qui entre. Vous respirez mieux (filtration des pollens, particules fines) et vous limitez les pertes thermiques. Surtout utile après une bonne isolation du logement.",
    badge: 'Cumul aides',
    badgeLabel: 'Aides cumulables, récupération de chaleur',
    keyPoints: [
      {
        iconName: 'wind',
        label: 'Efficacité thermique supérieure à 85 %',
        description: "Rendement thermique exigé pour bénéficier des aides en maison individuelle.",
      },
      {
        iconName: 'shield-check',
        label: "Qualité de l'air intérieur",
        description: "Filtration efficace de l'air entrant (pollens, particules) avec filtres lavables ou jetables.",
      },
      {
        iconName: 'thermometer',
        label: "Complément à l'isolation",
        description: "Pertinent quand le logement est bien isolé : sans ventilation efficace, l'isolation peut générer humidité et inconfort.",
      },
      {
        iconName: 'hard-hat',
        label: 'Professionnel qualifié',
        description: "Installation et dimensionnement par un professionnel certifié pour bénéficier des aides.",
      },
    ],
    cumulableWith: [MPR, CEE, ECO_PTZ, TVA_55],
    steps: [
      {
        title: 'Étude de faisabilité',
        description: "Le professionnel évalue le passage des gaines (combles, faux plafond), l'emplacement du caisson et l'adaptation au logement.",
      },
      {
        title: 'Demande des primes',
        description: "La demande de prime doit être faite avant la signature du devis.",
      },
      {
        title: 'Installation des réseaux et du caisson',
        description: "Pose des gaines d'insufflation/extraction dans toutes les pièces, raccordement au caisson échangeur de chaleur.",
      },
      {
        title: 'Versement des aides',
        description: "Les primes sont versées après réception du dossier complet.",
      },
    ],
    faq: [
      {
        question: 'VMC simple flux ou double flux : laquelle choisir ?',
        answer:
          "La VMC simple flux ne fait qu'extraire l'air vicié (peu coûteuse, ~ 500 à 1 500 €). La VMC double flux récupère la chaleur de l'air extrait pour préchauffer l'air neuf (3 000 à 7 000 €) : plus coûteuse mais beaucoup plus économe en énergie. La double flux est éligible aux meilleures aides.",
      },
      {
        question: 'Peut-on installer une VMC double flux en rénovation ?',
        answer:
          "Oui, mais c'est plus complexe qu'en construction neuve : il faut faire passer les gaines (combles, faux plafond, coffrages). En maison individuelle, c'est souvent faisable. En appartement existant, c'est plus délicat, et la VMC simple flux est parfois préférable.",
      },
      {
        question: 'Quel entretien pour une VMC double flux ?',
        answer:
          "Changement des filtres tous les 6 à 12 mois, nettoyage du caisson et de l'échangeur tous les 2 à 3 ans. Sans entretien, l'efficacité chute rapidement et l'hygiène de l'air se dégrade.",
      },
    ],
    ctaLabel: 'Calculer mes aides VMC double flux',
    ctaHref: '/particuliers/calculer-mes-aides',
    secondaryCta: { label: 'Voir la fiche BAR-TH-125', href: '/cee/fiches/BAR-TH-125' },
    effyPromise: 'Respirez un air sain sans perdre de chaleur',
    comfortBenefit: "Air filtré (pollens, particules) sans courants d'air",
    savingsPercent: 90,
    savingsLabel: "de chaleur récupérée sur l'air extrait",
    maxAidEur: 2500,
    avgCostEur: { min: 3000, max: 7000 },
  },
];

export function getTravailStub(slug: string): TravailStub | undefined {
  return TRAVAUX_PARTICULIERS.find((t) => t.slug === slug);
}

/* ─── Helpers tone → classes Tailwind ── */

export const CUMUL_TONE_CLASSES: Record<TravailCumulTone, { dot: string; chip: string }> = {
  brand: {
    dot: 'bg-brand-500',
    chip: 'bg-brand-50 text-brand-700 border-brand-200',
  },
  accent: {
    dot: 'bg-accent-500',
    chip: 'bg-accent-50 text-accent-700 border-accent-200',
  },
  violet: {
    dot: 'bg-violet-500',
    chip: 'bg-violet-50 text-violet-700 border-violet-200',
  },
  sky: {
    dot: 'bg-sky-500',
    chip: 'bg-sky-50 text-sky-700 border-sky-200',
  },
  amber: {
    dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-700 border-amber-200',
  },
};
